import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";
import OpenInApp from "./OpenInApp";
import { useAuth } from "../context/AuthContext";

const Navbar = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // A tap on a menu link navigates but leaves the panel sitting open over the
  // new page, so the route change is what closes it.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-20 border-b border-hairline bg-sand/85 backdrop-blur">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 font-display text-lg font-extrabold text-ink sm:text-xl"
        >
          <span
            aria-hidden="true"
            className="grid h-8 w-8 -rotate-3 place-items-center rounded-xl bg-apricot text-base font-extrabold text-white"
          >
            學
          </span>
          {t("brand")}
        </Link>

        <div className="hidden items-center gap-4 lg:flex xl:gap-6">
          <Link
            to="/features"
            className="font-display text-sm font-bold text-ink-soft hover:text-ink"
          >
            {t("nav.features")}
          </Link>
          <Link
            to="/pricing"
            className="font-display text-sm font-bold text-ink-soft hover:text-ink"
          >
            {t("nav.pricing")}
          </Link>
          <ThemeToggle />
          <LanguageSwitcher />
          {/* Held blank until the profile probe answers — rendering "Log in"
              first and swapping it for "Account" a beat later is worse than
              a brief gap. */}
          <div className="flex items-center gap-4">
            {loading ? null : isAuthenticated ? (
              <>
                <Link
                  to="/account"
                  className="font-display text-sm font-bold text-ink-soft hover:text-ink"
                >
                  {t("nav.account")}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="text-sm font-bold text-ink-soft hover:text-ink"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-bold text-ink-soft hover:text-ink"
                >
                  {t("nav.login")}
                </Link>
                <Link to="/register" className="btn px-4 py-2 text-sm">
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
          <OpenInApp className="btn-ghost px-4 py-2 text-sm" />
        </div>

        {/* Phone bar: the app link stays out of the panel — on a phone it is
            the one action that can actually hand off to the installed app. */}
        <div className="flex items-center gap-2 lg:hidden">
          <OpenInApp className="btn whitespace-nowrap px-3 py-2 text-xs" />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="site-menu"
            aria-label={t(open ? "nav.closeMenu" : "nav.menu")}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-hairline
                       bg-surface text-ink transition-colors hover:border-apricot focus:outline
                       focus:outline-2 focus:outline-apricot"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {open ? (
                <path d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {open && (
        <div id="site-menu" className="border-t border-hairline bg-sand lg:hidden">
          <div className="mx-auto flex max-w-6xl flex-col px-4 pb-4">
            <Link
              to="/features"
              className="border-b border-hairline py-3.5 font-display font-bold text-ink"
            >
              {t("nav.features")}
            </Link>
            <Link
              to="/pricing"
              className="border-b border-hairline py-3.5 font-display font-bold text-ink"
            >
              {t("nav.pricing")}
            </Link>
            {loading ? null : isAuthenticated ? (
              <>
                <Link
                  to="/account"
                  className="border-b border-hairline py-3.5 font-display font-bold text-ink"
                >
                  {t("nav.account")}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="border-b border-hairline py-3.5 text-left font-display font-bold text-ink"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="border-b border-hairline py-3.5 font-display font-bold text-ink"
                >
                  {t("nav.login")}
                </Link>
                <Link to="/register" className="btn mt-4 w-full">
                  {t("nav.register")}
                </Link>
              </>
            )}
            <div className="mt-4 flex items-center gap-3">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
